/**
 * Attachments: supporting files of records (bank updates, offers, invoices, payments, credit notes, revenue
 * adjustments / clawbacks, reconciliation runs, staff commission entries, employers, enlistment requests).
 * One mechanism, every owner type: GET / POST /attachments/{ownerType}/{ownerId}, DELETE …/{id}, GET …/{id}/download.
 * The server checks the owner's permission; the UI hides add / remove from callers without the manage permission.
 */
import type { Schema } from '@dealdesk/api-schema';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api, unwrap } from '@/shared/api/client';
import { STALE } from '@/shared/api/stale';
import { uploadFile } from '@/shared/api/upload';

export type Attachment = Schema<'Attachment'>;
export type AttachmentOwnerType = Schema<'AttachmentOwnerType'>;
export type Disposition = 'attachment' | 'inline';
export type DownloadLink = Schema<'DownloadLink'>;

export const attachmentKeys = {
  all: ['attachments'] as const,
  list: (ownerType: AttachmentOwnerType, ownerId: string) => ['attachments', ownerType, ownerId] as const,
};

export function useAttachments(ownerType: AttachmentOwnerType, ownerId: string, opts: { enabled?: boolean } = {}) {
  return useQuery({
    queryKey: attachmentKeys.list(ownerType, ownerId),
    queryFn: ({ signal }) => unwrap(api.GET('/attachments/{ownerType}/{ownerId}', { params: { path: { ownerType, ownerId } }, signal })),
    staleTime: STALE.record,
    enabled: opts.enabled ?? true,
  });
}

/** Uploads (purpose attachment) and attaches one file, or a new version of `replacesId`. Never optimistic. */
export function useAddAttachment(ownerType: AttachmentOwnerType, ownerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, note, replacesId, onProgress }: { file: File; note?: string; replacesId?: string; onProgress?: (p: number) => void }) => {
      const uploadId = await uploadFile(file, onProgress ?? (() => undefined), undefined, 'attachment');
      return unwrap(api.POST('/attachments/{ownerType}/{ownerId}', { params: { path: { ownerType, ownerId } }, body: { uploadId, ...(note ? { note } : {}), ...(replacesId ? { replacesId } : {}) } }));
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: attachmentKeys.list(ownerType, ownerId) }),
  });
}

/** Attaches files to a record that was just created (e.g. after logging a bank update or recording a payment). */
export async function attachFiles(ownerType: AttachmentOwnerType, ownerId: string, files: readonly File[]): Promise<void> {
  for (const file of files) {
    const uploadId = await uploadFile(file, () => undefined, undefined, 'attachment');
    await unwrap(api.POST('/attachments/{ownerType}/{ownerId}', { params: { path: { ownerType, ownerId } }, body: { uploadId } }));
  }
}

export function useRemoveAttachment(ownerType: AttachmentOwnerType, ownerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (attachmentId: string) => unwrap(api.DELETE('/attachments/{ownerType}/{ownerId}/{attachmentId}', { params: { path: { ownerType, ownerId, attachmentId } } })),
    onSuccess: () => void qc.invalidateQueries({ queryKey: attachmentKeys.list(ownerType, ownerId) }),
  });
}

export const fetchAttachmentLink = (ownerType: AttachmentOwnerType, ownerId: string, attachmentId: string, disposition: Disposition): Promise<DownloadLink> =>
  unwrap(api.GET('/attachments/{ownerType}/{ownerId}/{attachmentId}/download', { params: { path: { ownerType, ownerId, attachmentId }, query: { disposition } } }));
